import React, { useCallback, useRef, useState } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { InteractorFileUpload } from '../../interactors/Main'
import { useSelector } from 'react-redux'
import { showAlert } from '../../utility/SweetAlert'
import { validateFileType, validateMIMEType } from '../../utility/MimeType'
import PropTypes from 'prop-types'
import AvatarEditor from 'react-avatar-editor'
import { Close } from '@mui/icons-material'
import { InteractorProductMedia } from '../../interactors/Main'
import Compressor from 'compressorjs'

const Input = styled('input')({
  display: 'none',
})

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  width: '80vw',
  p: 4,
}

const imageStyle = {
  circle: {
    borderRadius: '50%',
    backgroundColor: '#F5F5F5',
    height: '7rem',
    minWidth: '7rem',
    maxWidth: '7rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  roundedRectangle: {
    borderRadius: '12px',
    backgroundColor: '#F5F5F5',
    height: '6rem',
    minWidth: '8rem',
    maxWidth: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  square: {
    backgroundColor: '#F5F5F5',
    height: '8rem',
    minWidth: '8rem',
    maxWidth: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
}

const boxStyle = {
  circle: {
    border: '2px dotted #C4C4C4',
    borderRadius: '50%',
    backgroundColor: '#F5F5F5',
    height: '7rem',
    width: '7rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  roundedRectangle: {
    border: '2px dotted #C4C4C4',
    borderRadius: '12px',
    backgroundColor: '#F5F5F5',
    height: '6rem',
    minWidth: '8rem',
    maxWidth: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  square: {
    border: '2px dotted #C4C4C4',
    backgroundColor: '#F5F5F5',
    height: '8rem',
    width: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
}
const UploadImageCrop = (props) => {
  const {
    labelName, // (String) Label Name for the text field
    accept, // (String) Accepted file extensions, begins with . (dot) separated with , (comma) (eg. .jpg,.png)
    required = false, // (Bool) True if this field is required on the form
    value, // (Object) Default value for this upload image (must have filename and extension attribute)
    name, // (String) Name of the input
    onFieldSet, // (Function) function when the field is set
    shape = 'roundedRectangle', // (String) The shape of the component (available: circle, roundedRectangle, square)
    error = '', // (String) The error message
    type = 'PUBLIC', // (String) Media type
    category = 'PRODUCT', // (String) Media Category
    referenceUid = null, // (String) Uid reference where this media is a part of
    referenceSequence = 0, // (Number) Optional, Sequence if there's multiple of this component on the parent
    setLoadingParent,
  } = props
  const { t } = useTranslation()
  const theme = useTheme()
  const matchDownSM = useMediaQuery(theme.breakpoints.down('md'))
  const imageEditor = useRef(null)
  const { uploadFile } = InteractorFileUpload()
  const { account } = useSelector((state) => state.account)
  const [file, setFile] = useState(value.filename)
  const [loading, setLoading] = useState(false)
  const [scale, setScale] = useState(1)
  const [croppedImage, setCroppedImage] = useState(value.filename)
  const { productMediaLoading, addProductMedia, deleteProductMedia, updateProductMedia } =
    InteractorProductMedia()

  const handleUploadFile = useCallback(
    async (e) => {
      setLoading(true)
      setLoadingParent(true)
      const files = e.target.files[0]

      if (files.size <= 0) {
        setLoading(false)
        return showAlert({
          icon: 'error',
          message: t('message.file_corrupted'),
          timer: 2000,
        })
      }
      if (!validateFileType(e.target.files[0], accept)) {
        setLoading(false)
        return showAlert({ icon: 'error', message: t('message.type_not_supported'), timer: 2000 })
      }
      if (files.size > process.env.REACT_APP_MAX_FILE_UPLOAD_MB * 1024 * 1024) {
        setLoading(false)
        return showAlert({ icon: 'error', message: t('message.file_too_big'), timer: 2000 })
      }
      if (!(await validateMIMEType(e.target.files[0]))) {
        setLoading(false)
        return showAlert({
          icon: 'error',
          message: t('message.malicious_file_detected'),
          timer: 2000,
        })
      }

      setFile(e.target.files[0])
      setCroppedImage(null)
    },
    [accept, setLoadingParent, t],
  )

  const handleSave = useCallback(() => {
    const img = imageEditor.current?.getImageScaledToCanvas().toDataURL()

    if (!img) return

    setCroppedImage(img)

    fetch(img)
      .then((res) => res.arrayBuffer())
      .then((buf) => {
        const convertedBase = new File(
          [buf],
          `${Date.now().toString()}.${img.split(';')[0].split(':')[1].split('/')[1]}`,
          {
            type: img.split(';')[0].split(':')[1],
          },
        )
        new Compressor(convertedBase, {
          quality: 0.8,
          height: 600,
          width: 600,
          convertSize: 200000,
          success: async (compressedResult) => {
            await uploadFile(
              {
                type: type,
                category: category,
                file: new File(
                  [compressedResult],
                  `${Date.now().toString()}.${img.split(';')[0].split(':')[1].split('/')[1]}`,
                  {
                    type: img.split(';')[0].split(':')[1],
                  },
                ),
                reference_uid: account.uid,
              },
              (response) => {
                setLoading(false)
                setFile(response.data[0].url)
                if (referenceUid) {
                  if (value?.filename) {
                    updateProductMedia({
                      uid: value?.uid,
                      product_uid: referenceUid,
                      product_media_category: 'IMAGE',
                      product_media_type: convertedBase.type.split('/').pop().toUpperCase(),
                      product_media_url: response.data[0].url,
                      product_media_sort_number: referenceSequence,
                      updated_by: account.full_name,
                    })
                  } else {
                    addProductMedia({
                      product_uid: referenceUid,
                      product_media_category: 'IMAGE',
                      product_media_type: convertedBase.type.split('/').pop().toUpperCase(),
                      product_media_url: response.data[0].url,
                      product_media_sort_number: referenceSequence,
                      created_by: account.full_name,
                    })
                  }
                  window.location.reload()
                }
                setLoadingParent(false)
                return onFieldSet(name, {
                  filename: response.data[0].url,
                  extension: convertedBase.name.split('.').pop(),
                })
              },
            )
          },
        })
      })
  }, [
    account.full_name,
    account.uid,
    addProductMedia,
    category,
    name,
    onFieldSet,
    referenceSequence,
    referenceUid,
    setLoadingParent,
    type,
    updateProductMedia,
    uploadFile,
    value?.filename,
    value?.uid,
  ])

  const handleCancelFile = () => {
    setFile(null)
    setCroppedImage(null)
    if (value.uid) {
      deleteProductMedia({
        uid: value.uid,
        updatedBy: account.full_name,
      })
    }
    setLoading(false)
    onFieldSet(name, '')
  }

  const handleCancelModal = () => {
    setFile(value.filename)
    setCroppedImage(value.filename)
    setLoadingParent(false)
    setLoading(false)
  }

  return (
    <Box sx={{ maxWidth: '10rem' }}>
      {croppedImage ? (
        <>
          {value.product_media_sort_number !== 1 && (
            <IconButton
              onClick={handleCancelFile}
              component="span"
              disableRipple
              sx={{
                position: 'absolute',
                float: 'right',
                backgroundColor: 'white',
                padding: 0,
                margin: '5px 5px',
              }}
            >
              <Close />
            </IconButton>
          )}
          <label htmlFor={`icon-button-file-${labelName}`}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-start',
                flexDirection: 'column',
              }}
            >
              <Input
                accept={accept}
                id={`icon-button-file-${labelName}`}
                type="file"
                key={Date.now()}
                onChange={handleUploadFile}
                onClick={(event) => {
                  event.target.value = null
                }}
              />
              <img src={croppedImage} alt="Test" style={imageStyle[shape]} />
              <Typography textAlign={'center'} sx={{ margin: 'auto' }}>
                {labelName} {required && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            </div>
          </label>
        </>
      ) : file ? (
        <Modal
          open={file?.name?.length > 0}
          onClose={() => {
            handleCancelModal()
          }}
          sx={{
            overflow: 'scroll',
            height: '100%',
          }}
        >
          <Grid
            container
            sx={modalStyle}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Grid container item xs={12} display="flex" justifyContent="center">
              <Grid item xs={9} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    width: { xs: '100%' },
                    padding: { xs: '10px', l: '1rem' },
                  }}
                >
                  {t('message.product_validate')}
                </Typography>
              </Grid>
              <Grid item xs={3} display="flex" justifyContent="flex-end">
                <Button onClick={handleCancelModal}>
                  <Close style={{ color: 'black' }} />
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <AvatarEditor
                ref={imageEditor}
                image={file}
                width={600}
                height={600}
                border={40}
                scale={scale}
                style={{
                  width: matchDownSM ? '80vw' : '600px',
                  height: matchDownSM ? '80vw' : '600px',
                }}
                backgroundColor="#FFF"
              />
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <input
                name="scale"
                type="range"
                onChange={(e) => {
                  setScale(+e.target.value)
                }}
                min={0.1}
                max={2}
                step={0.01}
                style={{ width: '100%' }}
                defaultValue={scale}
              />
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <Button
                disableRipple
                disableTouchRipple
                variant="contained"
                type="primary"
                onClick={handleSave}
                sx={{
                  borderRadius: '2rem',
                  backgroundColor: theme.palette.primary.main,
                  boxShadow: 'none',
                  color: 'white',
                  margin: '0 5px',
                  ':hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                  width: '100%',
                }}
              >
                {t('button.set')}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      ) : (
        <>
          <label htmlFor={`icon-button-file-${labelName}`}>
            <Input
              accept={accept}
              id={`icon-button-file-${labelName}`}
              type="file"
              key={Date.now()}
              onChange={handleUploadFile}
              onClick={(event) => {
                event.target.value = null
              }}
            />
            <Box sx={boxStyle[shape]}>
              <Typography component={'span'} sx={{ color: '#C4C4C4' }}>
                {loading || productMediaLoading ? <CircularProgress /> : labelName}{' '}
                {required && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            </Box>
          </label>
          {required && error ? (
            <span style={{ fontSize: '12px', color: 'red' }}>{error}</span>
          ) : (
            <span></span>
          )}
        </>
      )}
    </Box>
  )
}

UploadImageCrop.propTypes = {
  accept: PropTypes.string,
  category: PropTypes.string,
  error: PropTypes.any,
  labelName: PropTypes.string,
  key: PropTypes.string,
  name: PropTypes.string,
  onFieldSet: PropTypes.func,
  referenceSequence: PropTypes.number,
  referenceUid: PropTypes.string,
  required: PropTypes.bool,
  shape: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.any,
  loadingParent: PropTypes.bool,
  setLoadingParent: PropTypes.func,
}

export default UploadImageCrop
